// Turns the 2D viewer SVGs into a <canvas>.
// An SVG drawn as an image loads no external files, so every <image href> is inlined first.
// All hrefs are same-origin (public/), so the canvas is not tainted.

// One fetch per URL per page: radars and icons do not change.
const dataUrls = new Map();

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function inlineUrl(url) {
  if (!dataUrls.has(url)) {
    const pending = fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`Could not fetch ${url}: ${response.status}`);
        return response.blob();
      })
      .then(blobToDataUrl);
    // A failed fetch is retried on the next capture.
    pending.catch(() => dataUrls.delete(url));
    dataUrls.set(url, pending);
  }
  return dataUrls.get(url);
}

export function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Could not load image ${src.slice(0, 80)}`));
    image.src = src;
  });
}

async function svgToImage(svg, width, height) {
  const clone = svg.cloneNode(true);
  clone.setAttribute('width', width);
  clone.setAttribute('height', height);
  await Promise.all([...clone.querySelectorAll('image[href]')].map(async (image) => {
    const href = image.getAttribute('href');
    if (!href.startsWith('data:')) image.setAttribute('href', await inlineUrl(href));
  }));

  const markup = new XMLSerializer().serializeToString(clone);
  const url = URL.createObjectURL(new Blob([markup], { type: 'image/svg+xml' }));
  try {
    return await loadImage(url);
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function rasterizeViewer({
  svg, overlay, width, height, scale, background,
}) {
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  const ctx = canvas.getContext('2d');

  if (background) {
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }

  // The pen overlay goes on top of the map, as on screen.
  const layers = await Promise.all(
    [svg, overlay].filter(Boolean).map((layer) => svgToImage(layer, width, height)),
  );
  layers.forEach((image) => ctx.drawImage(image, 0, 0, canvas.width, canvas.height));
  return canvas;
}
